import { useUser } from "@/queries/user"
import { Link, type LinkProps, router } from "expo-router"
import { TouchableOpacity, View } from "react-native"
import { useSafeAreaInsets } from "react-native-safe-area-context"
import BackButton from "@/assets/icons/back-arrow-dark.svg"
import ChevronRight from "@/assets/icons/chevron-right.svg"
import { formatPhoneNumberString } from "@/utils/StringUtils"
import * as AccountEvents from "@/analytics/events/account"
import { useViewedCallback } from "@/analytics/react"
import { setStatusBarStyle } from "@/stores/statusBarStyle"
import useDeviceAttributes from "@/hooks/useDeviceAttributes"
import BFText from "@/components/standard/BFText"

const onBack = () => {
  router.back()
}

type MenuItemProps = LinkProps & {
  menuItemTitle: string
  menuItemValue: string
}

const MenuItem: React.FC<MenuItemProps> = ({
  menuItemTitle,
  menuItemValue,
  ...props
}: MenuItemProps) => {
  return (
    <View className="px-4 py-6 border-b border-light-grey">
      <Link {...props} asChild>
        <TouchableOpacity
          testID={`menu-item-${menuItemValue}`}
          className="flex flex-row items-center justify-between"
        >
          <View className="flex flex-col justify-start flex-1 gap-2">
            <BFText className="text-lg font-figtree-bold">
              {menuItemTitle}
            </BFText>
            <BFText className="font-spacegrotesk">{menuItemValue}</BFText>
          </View>
          <ChevronRight className="text-dark-grey" />
        </TouchableOpacity>
      </Link>
    </View>
  )
}

export default function PersonalInfoIndexScreen() {
  const { data, isLoading } = useUser()
  const insets = useSafeAreaInsets()
  const { isWeb } = useDeviceAttributes()

  useViewedCallback(() => {
    setStatusBarStyle("dark")
    AccountEvents.accountEditPersonalInfoViewed()
  })

  if (isLoading || !data?.me) {
    return
  }

  const { me: userProfile } = data

  const formattedPhoneNumber = formatPhoneNumberString(userProfile.phoneNumber)

  return (
    <View
      className="flex flex-col justify-start flex-1 w-full bg-white"
      style={{ paddingTop: isWeb ? insets.top + 10 : insets.top }}
    >
      <View className="flex flex-col px-4 pb-4 border-b shrink-0 border-light-grey">
        <TouchableOpacity testID={"back-button"} onPress={onBack}>
          <BackButton />
        </TouchableOpacity>
        <BFText
          className={`text-[24px] font-figtree-bold leading-[24px] text-midnight-blue pt-4`}
        >
          Edit Personal Info
        </BFText>
      </View>
      <View className="flex flex-col justify-start size-full">
        <MenuItem
          href={"/account/personal-info/name"}
          menuItemTitle={"Name"}
          menuItemValue={`${userProfile.firstName} ${userProfile.lastName}`}
        />
        <MenuItem
          href={"/account/personal-info/email"}
          menuItemTitle={"Email"}
          menuItemValue={`${userProfile.email}`}
        />
        <MenuItem
          href={"/account/personal-info/phone"}
          menuItemTitle={"Phone"}
          menuItemValue={`${formattedPhoneNumber}`}
        />
      </View>
    </View>
  )
}
